import {useContext, useMemo} from 'react';
import './additional-providers-table.scss';
import {NetworkContext} from '../../lib/context';
import {observer} from 'mobx-react';
import {memo} from 'react';
import {useProviderPagination} from './hooks/use-provider-pagination';
import {useProviderSelection} from './hooks/use-provider-selection';
import {useProviderTableSort} from './hooks/use-provider-table-sort';
import {useScrollToProvider} from './hooks/use-scroll-to-provider';
import {TableHeader as ExtendedTableHeader, AdditionalProvidersTableProps} from './types';
import {getAdditionalProvidersTableHeaders} from './table-headers';
import ProviderTableShell from './provider-table-shell';

const AdditionalProvidersTable = memo(observer(({setSaveRosterModalOpen}: AdditionalProvidersTableProps) => {
    const networkOptimizationStore = useContext(NetworkContext);

    const {localProviders, existingProviders, existingProviderQueuedForDeletion, filters, filterProviderList} = networkOptimizationStore;

    // Use custom hooks to encapsulate business logic
    const {
        loading,
        error,
        currentPage,
        totalPages,
        loadingMore,
        loadMoreProviders,
        getLoadMoreButtonText,
        getRecordCountText
    } = useProviderPagination({networkOptimizationStore});

    const {
        selection,
        deselection,
        selectionSet,
        deselectionSet,
        existingProviderIds,
        handleCheckboxChange
    } = useProviderSelection({
        networkOptimizationStore,
        existingProviders
    });

    const {
        sortColumn,
        sortDirection,
        tableRows,
        handleSort
    } = useProviderTableSort({
        localProviders,
        existingProviders,
        filterProviderList: filterProviderList.bind(networkOptimizationStore),
        specialtyFilter: networkOptimizationStore.specialtyFilter || '',
        filters
    });

    const {scrollToExistingProvider} = useScrollToProvider({existingProviderQueuedForDeletion});

    // Define table headers
    const tableHeaders: ExtendedTableHeader[] = useMemo(
        () => getAdditionalProvidersTableHeaders(networkOptimizationStore.specialtyFilter),
        [networkOptimizationStore.specialtyFilter]
    );

    return (
        <ProviderTableShell
            loading={loading}
            error={error}
            tableRows={tableRows}
            tableHeaders={tableHeaders}
            loadMoreConfig={{
                currentPage,
                totalPages,
                loadingMore,
                buttonText: getLoadMoreButtonText(),
                recordCountText: getRecordCountText(),
                onLoadMore: loadMoreProviders
            }}
            skeletonRowCount={5}
            specialtyFilter={networkOptimizationStore.specialtyFilter}
            networkOptimizationStore={networkOptimizationStore}
            setSaveRosterModalOpen={setSaveRosterModalOpen}
            selection={selection}
            deselection={deselection}
            selectionSet={selectionSet}
            deselectionSet={deselectionSet}
            existingProviderIds={existingProviderIds}
            handleCheckboxChange={handleCheckboxChange}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            handleSort={handleSort}
            scrollToExistingProvider={scrollToExistingProvider}
        />
    );
}));

AdditionalProvidersTable.displayName = 'AdditionalProvidersTable';

export default AdditionalProvidersTable;